import type { TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import type { StatementCondition } from "../../../../../api/types"
import {
  cellKey,
  type StateContext,
  type WorklistIssue,
} from "../../studio/cellState"
import { normalizeCas } from "../../studio/conditionRows"
import { conditionSpec } from "../../studio/conditions"
import { substanceDisplayName } from "../reviewmask/substanceDisplayName"

/** One substance a needs-input issue is waiting on, with every field it lacks. */
export interface OpenPointGap {
  /** Normalized CAS, or the bare registry id; null when the finding names none. */
  substance: string | null
  /** The extracted substance name, when the statement discloses one. */
  name: string | null
  fields: string[]
  /** The finding identifies no disclosure (`RequiredGap.anonymous`): no name is
   *  borrowed and no chip is marked for it. */
  anonymous: boolean
}

/** What exactly keeps a step-3 issue open, read off the same issue the gate
 *  counts. Null for every other issue: their reason copy already says it all. */
export type OpenPointDetail =
  | { kind: "gaps"; gaps: OpenPointGap[] }
  | { kind: "unchecked"; open: number; total: number }
  | { kind: "edited"; open: number }

const substanceKey = (value: string | null): string | null =>
  normalizeCas(value) ?? (value || null)

export function openPointDetail(
  issue: WorklistIssue,
  ctx: StateContext,
  conditions: readonly StatementCondition[],
): OpenPointDetail | null {
  if (issue.unit !== "statement" || issue.step !== 3) return null
  if (issue.state === "needs-input") {
    const bySubstance = new Map<string, OpenPointGap>()
    for (const article of issue.articleIds) {
      for (const gap of ctx.requiredGaps?.get(
        cellKey(issue.framework, article),
      ) ?? []) {
        const substance = gap.anonymous ? null : substanceKey(gap.substance)
        const id = gap.anonymous ? "anonymous" : `substance:${substance}`
        const entry = bySubstance.get(id) ?? {
          substance,
          name: gap.anonymous
            ? null
            : (conditions.find((c) => substanceKey(c.cas) === substance)
                ?.substance ?? null),
          fields: [],
          anonymous: gap.anonymous,
        }
        for (const field of gap.fields)
          if (!entry.fields.includes(field)) entry.fields.push(field)
        bySubstance.set(id, entry)
      }
    }
    return bySubstance.size > 0
      ? { kind: "gaps", gaps: [...bySubstance.values()] }
      : null
  }
  if (issue.state !== "needs-verification") return null
  const open = issue.articleIds.length
  const edited = issue.articleIds.filter((a) =>
    ctx.humanEdited.has(cellKey(issue.framework, a)),
  ).length
  if (open > 0 && edited === open) return { kind: "edited", open }
  const total =
    ctx.statements.find((s) => s.id === issue.statementId)?.selectedArticles
      .length ?? 0
  return total > 0 ? { kind: "unchecked", open, total } : null
}

/** Does this chip's substance have an open required field on its card? */
export function substanceGapOpen(
  detail: OpenPointDetail | null | undefined,
  chip: { key: string; cas: string | null },
): boolean {
  if (detail?.kind !== "gaps") return false
  const key = substanceKey(chip.cas) ?? chip.key
  return detail.gaps.some(
    (g) =>
      !g.anonymous &&
      (g.substance === null ? chip.cas === null : g.substance === key),
  )
}

type Translate = (
  key: TranslationKeys,
  values?: Record<string, string | number>,
) => string

function fieldLabel(framework: string, field: string): TranslationKeys | null {
  if (field === "<unresolved-substance>")
    return "MC_DECL_V2_OPEN_POINT_FIELD_SUBSTANCE"
  const spec = conditionSpec(framework)
  if (field === "concentration") return spec.concentration?.label ?? null
  return spec.fields.find((f) => f.key === field)?.label ?? null
}

/** The open-points row text: a gap replaces the step `reason` with what is
 *  missing, a count is appended to it, and anything else keeps it as it is
 *  (a gap naming only fields the mask has no label for, e.g. `<result_spec>`). */
export function openPointDetailText(
  detail: OpenPointDetail | null | undefined,
  framework: string,
  reason: string,
  translate: Translate,
): string {
  if (!detail) return reason
  if (detail.kind === "unchecked")
    return `${reason} · ${translate("MC_DECL_V2_OPEN_POINT_UNCHECKED", {
      open: detail.open,
      total: detail.total,
    })}`
  if (detail.kind === "edited")
    return `${reason} · ${translate("MC_DECL_V2_OPEN_POINT_EDITED", { count: detail.open })}`
  const lines = detail.gaps.flatMap((gap) => {
    const labels = gap.fields
      .map((f) => fieldLabel(framework, f))
      .filter((k): k is TranslationKeys => k !== null)
      .map((k) => translate(k))
    if (labels.length === 0) return []
    const fields = [...new Set(labels)].join(", ")
    const substance =
      substanceDisplayName(gap.name ?? "", gap.substance, translate) ||
      gap.substance
    return [
      substance
        ? translate("MC_DECL_V2_OPEN_POINT_GAP", { substance, fields })
        : translate("MC_DECL_V2_OPEN_POINT_GAP_FIELDS", { fields }),
    ]
  })
  if (lines.length === 0) return reason
  return lines.length === 1
    ? lines[0]
    : `${lines[0]} · ${translate("MC_DECL_V2_OPEN_POINT_MORE", { count: lines.length - 1 })}`
}
